//================================================================
//  Tab: TechnicalArtefacts
//================================================================

//  Purpose: This tab displays all 'TechnicalArtefacts' of an Application

//  Example:
//    <TechnicalArtefacts></TechnicalArtefacts>    

//================================================================

//Libraries
import React, { useContext, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

//Contexts
import { GetUser } from '../../../../../Library/GlobalContexts';

//Components
import PageComponent from '../../../../../Components/PageComponent/PageComponent';
import LiveField from '../../../../../Components/LiveField/LiveField';

//Functions
import DocumentListener from '../../../../../Library/DocumentListener';

//Images


export default function TechnicalArtefacts() {

  //------------------------------------------------------
  //  useContext & React Router
  //------------------------------------------------------

  const getUser = useContext(GetUser);
  const params = useParams();

  //------------------------------------------------------
  //  useStates
  //------------------------------------------------------

  // Used to save page status > 'pending', 'onload', 'error-invalid', 'error-fatal'
  const [pageStatus, setPageStatus] = useState('pending');

  // Holds the application document from 
  const [applicationDoc, setApplicationDoc] = useState();

  //------------------------------------------------------
  //  Functions
  //------------------------------------------------------

  //------------------------------------------------------
  //  useEffects
  //------------------------------------------------------

  // Query Listener
  // - Get all Overview with the status of 'Active'
  useEffect(() => {

    const applicationId = params.id;

    if (applicationId === '') return;
    if (applicationId === null) return;

    function onLoadChange(document) {

      if (document === undefined) return setPageStatus('forbidden');

      setApplicationDoc(document);
      setPageStatus('onload')

    }

    function onError(error) {

      console.log(error);
      setPageStatus('error-fatal');

    }

    const unsubscribe = DocumentListener('applications', applicationId, onLoadChange, onLoadChange, onError);

    return () => {
      unsubscribe();
    };

    // eslint-disable-next-line
  }, [getUser]);

  //------------------------------------------------------
  //  HTML
  //------------------------------------------------------

  return (
    <PageComponent
      status={pageStatus}
      body={
        <div className='flex flex-col w-[50%] px-[20px]'>

          {/* Application Type */}
          <LiveField
            type='choice'
            collectionid='applications'
            documentid={applicationDoc?.applicationid}
            fieldpath={'type'}
            label={<label className='font-medium'>Application Type</label>}
            defaultValue={applicationDoc?.type}
            options={[
              'On-Prem/Custom',
              'IaaS',
              'PaaS',
              'SaaS',
              'Cloud',
              'Hybrid',
              'N/A'
            ]}
          ></LiveField>

          {/* Deployment Name */}
          <LiveField
            type='text'
            collectionid='applications'
            documentid={applicationDoc?.applicationid}
            fieldpath={'deploymentname'}
            label={<label className='font-medium'>Deployment Name</label>}
            defaultValue={applicationDoc?.deploymentname}
            placeholder={'The name of the licenced instanced, Aconex AU1, etc'}
          ></LiveField>

          {/* Deployment Type */}
          <LiveField
            type='multiselect'
            collectionid='applications'
            documentid={applicationDoc?.applicationid}
            fieldpath={'deploymenttype'}
            label={<label className='font-medium'>Deployment Type</label>}
            defaultValue={applicationDoc?.deploymenttype}
            options={['Web Application', 'Desktop Application', 'Mobile Application', 'Server Application', 'N/A']}
          ></LiveField>

          {/* SSO Implemented */}
          <LiveField
            type='choice'
            collectionid='applications'
            documentid={applicationDoc?.applicationid}
            fieldpath={'ssoimplemented'}
            label={<label className='font-medium'>SSO Implemented</label>}
            defaultValue={applicationDoc?.ssoimplemented}
            options={[
              'Yes',
              'No',
              'N/A',
            ]}
          ></LiveField>

          {/* MFA Implemented */}
          <LiveField
            type='choice'
            collectionid='applications'
            documentid={applicationDoc?.applicationid}
            fieldpath={'mfaimplemented'}
            label={<label className='font-medium'>MFA Implemented</label>}
            defaultValue={applicationDoc?.mfaimplemented}
            options={[
              'Yes',
              'No',
              'N/A',
            ]}
          ></LiveField>

          {/* Environment Details */}
          <LiveField
            type='multiselect'
            collectionid='applications'
            documentid={applicationDoc?.applicationid}
            fieldpath={'environment'}
            label={<label className='font-medium'>Environment Details</label>}
            defaultValue={applicationDoc?.environment}
            options={[
              'Prod',
              'UAT/Pre-prod',
              'SIT',
              'Dev',
              'DR',
            ]}
          ></LiveField>

          {/* App Server Details */}
          <LiveField
            type='multiline'
            collectionid='applications'
            documentid={applicationDoc?.applicationid}
            fieldpath={'appserver'}
            label={<label className='font-medium'>App Server Details</label>}
            defaultValue={applicationDoc?.appserver}
            placeholder={'List of servers related to this application - All Environments'}
            validation={{
              required: true,
              type: 'text',
            }}
          ></LiveField>

          {/* Other Server Details */}
          <LiveField
            type='multiline'
            collectionid='applications'
            documentid={applicationDoc?.applicationid}
            fieldpath={'otherserver'}
            label={<label className='font-medium'>Other Server Details</label>}
            defaultValue={applicationDoc?.otherserver}
            placeholder={'List of other servers related to this application database, web'}
          ></LiveField>

          {/* Other Software Requirements */}
          <LiveField
            type='multiline'
            collectionid='applications'
            documentid={applicationDoc?.applicationid}
            fieldpath={'softwarerequirements'}
            label={<label className='font-medium'>Other Software Requirements</label>}
            defaultValue={applicationDoc?.softwarerequirements}
            placeholder={'List of pre-requisite software for the application Java, .net etc'}
          ></LiveField>

          {/* DNS Names */}
          <LiveField
            type='list'
            collectionid='applications'
            documentid={applicationDoc?.applicationid}
            fieldpath={'dnsnames'}
            label={<label className='font-medium'>DNS Names</label>}
            defaultValue={applicationDoc?.dnsnames}
            placeholder={'DNS names/records attached'}
          ></LiveField>

          {/* Service Accounts */}
          <LiveField
            type='list'
            collectionid='applications'
            documentid={applicationDoc?.applicationid}
            fieldpath={'serviceaccounts'}
            label={<label className='font-medium'>Service Accounts</label>}
            defaultValue={applicationDoc?.serviceaccounts}
            placeholder={'Add any Service Accounts'}
          ></LiveField>

          {/* GCP Project */}
          <LiveField
            type='list'
            collectionid='applications'
            documentid={applicationDoc?.applicationid}
            fieldpath={'gcpproject'}
            label={<label className='font-medium'>GCP Project</label>}
            defaultValue={applicationDoc?.gcpproject}
            placeholder={'Name of GCP projects related to application'}
          ></LiveField>

          {/* Primary Hosting Location */}
          <LiveField
            type='text'
            collectionid='applications'
            documentid={applicationDoc?.applicationid}
            fieldpath={'primaryhostinglocation'}
            label={<label className='font-medium'>Primary Hosting Location</label>}
            defaultValue={applicationDoc?.primaryhostinglocation}
            placeholder={'Region where the application is hosted'}
            validation={{
              required: true,
              type: 'text',
            }}
          ></LiveField>

          {/* Secondary Hosting Location */}
          <LiveField
            type='text'
            collectionid='applications'
            documentid={applicationDoc?.applicationid}
            fieldpath={'secondaryhostinglocation'}
            label={<label className='font-medium'>Secondary Hosting Location</label>}
            defaultValue={applicationDoc?.secondaryhostinglocation}
            placeholder={'Region where the application is hosted'}
          ></LiveField>

          {/* Application URL */}
          <LiveField
            type='text'
            collectionid='applications'
            documentid={applicationDoc?.applicationid}
            fieldpath={'applicationurl'}
            label={<label className='font-medium'>Application URL</label>}
            defaultValue={applicationDoc?.applicationurl}
            placeholder={'Web link to access the application'}
            validation={{
              type: 'link'
            }}
          ></LiveField>

          {/* Operating System and Version */}
          <LiveField
            type='text'
            collectionid='applications'
            documentid={applicationDoc?.applicationid}
            fieldpath={'osversion'}
            label={<label className='font-medium'>Operating System and Version</label>}
            defaultValue={applicationDoc?.osversion}
            placeholder={'Operating system of the application with Version'}
          ></LiveField>

          {/* Internet Facing */}
          <LiveField
            type='choice'
            collectionid='applications'
            documentid={applicationDoc?.applicationid}
            fieldpath={'internetfacing'}
            label={<label className='font-medium'>Internet Facing</label>}
            defaultValue={applicationDoc?.internetfacing}
            options={[
              'Yes',
              'No',
              'N/A',
            ]}
          ></LiveField>

        </div>
      }
    ></PageComponent>
  )
}
